import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useTranslation } from 'react-i18next';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { Button, Loader } from 'ncoded-component-library';
import ListingContext from '../../providers/Listing/Listing.context';
import StepsContext from 'providers/Steps/Steps.context';
import { useLocation } from 'react-router-dom';
import utils, { checkIsUrgent, scrollToErrors, sentryError } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import AuthContext from 'providers/Auth/Auth.context';
import {
  HIDE_CONTROLS_PAGES,
  processLCWValuesBeforeSubmit,
  validateLCW,
} from 'router/subrouters/ListingCreationWizard/utils';

import './ListingEditStepWrapper.styles.scss';

type ListingEditStepWrapperProps = {
  className?: string;
};

const ListingEditStepWrapper: React.FC<ListingEditStepWrapperProps> = (
  props,
) => {
  const { children, className } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { navigateBack } = useContext(AuthContext);
  const { nextStep, currentStep, steps, prevStep } = useContext(StepsContext);
  const {
    patchListing,
    listingFormValues,
    listing: { type: listingType },
  } = useContext(ListingContext);

  const isLastStep = useMemo(
    () => currentStep + 1 === steps.length,
    [currentStep, steps.length],
  );

  const classes = classNames('listing-edit-step-wrapper', className);

  const { pathname } = useLocation();

  const { t } = useTranslation();

  // PATCH LISTING : EDIT LISTING
  const patchListingSubmit = useCallback(
    async (values) => {
      const { appointmentType, ...restValues } = values;

      const processedValues = processLCWValuesBeforeSubmit(restValues);

      const patchValues = utils.changePropertyValueWith(
        {
          ...restValues,
          ...processedValues,
          ...(currentStep === 0 && checkIsUrgent(listingType)
            ? { appointmentType }
            : {}),
        },
        null,
        undefined,
      );

      try {
        await patchListing(patchValues);
        isLastStep ? navigateBack() : nextStep({ replace: true });
        popSuccess(t(SUCCESS_MESSAGES.PACHED_LISTING));
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [
      currentStep,
      isLastStep,
      listingType,
      navigateBack,
      nextStep,
      patchListing,
      popServerError,
      popSuccess,
      t,
    ],
  );

  const hideControls = useMemo(
    () => HIDE_CONTROLS_PAGES.some((el) => pathname.includes(el)),
    [pathname],
  );

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={patchListingSubmit}
      initialValues={listingFormValues}
      mutators={{ ...arrayMutators }}
      validate={validateLCW}
      render={({ submitting, invalid, form: { submit }, handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit} className={classes}>
            {children}
            {!hideControls && (
              <div className="listing-edit-step-wrapper__buttons-container">
                <div className="listing-edit-step-wrapper__buttons">
                  <div>
                    {currentStep !== 0 && (
                      <Button
                        type="button"
                        variant="link"
                        className="back-button"
                        icon={ArrowleftIcon}
                        onClick={() => prevStep({ replace: true })}
                      />
                    )}
                  </div>

                  <Button
                    type="submit"
                    iconPosition="right"
                    className="next-button"
                    icon={ArrowrightIcon}
                    onClick={() => {
                      submit();

                      if (invalid) {
                        scrollToErrors();
                      }
                    }}
                  >
                    {t(isLastStep ? 'save' : 'next')}
                  </Button>
                </div>
              </div>
            )}
            {submitting && <Loader className="listing-edit-form-loader" />}
          </form>
        );
      }}
    />
  );
};

export default ListingEditStepWrapper;
